import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchFuelTransactions,
  fetchFuelTransactionById,
  createFuelTransaction,
  updateFuelTransaction,
  deleteFuelTransaction,
  restoreFuelTransaction,
  fetchAircraftFuelSummary,
  fetchFuelCommitmentProgress,
} from '@/lib/api/fuel-transactions'
import { broadcastInvalidation } from '@/lib/sync'
import type { FuelTransactionFormData, FuelTransactionFilters } from '@/types/fuel-transactions'
import type { RecordStatusFilter } from '@/types/record-status'
import { toast } from 'sonner'

/**
 * Query key factory for fuel transactions
 * Provides consistent keys for cache management
 */
export const fuelTransactionKeys = {
  all: ['fuel-transactions'] as const,
  lists: () => [...fuelTransactionKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: FuelTransactionFilters, recordStatus?: RecordStatusFilter) =>
    [...fuelTransactionKeys.lists(), organizationId, filters, recordStatus ?? 'active'] as const,
  details: () => [...fuelTransactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...fuelTransactionKeys.details(), id] as const,
  // Aircraft fuel summary (for FuelTab)
  summaries: () => [...fuelTransactionKeys.all, 'summary'] as const,
  summary: (aircraftId: string, contractId?: string) =>
    [...fuelTransactionKeys.summaries(), aircraftId, contractId] as const,
  // Commitment progress (for dashboards)
  commitments: () => [...fuelTransactionKeys.all, 'commitment'] as const,
  commitment: (organizationId: string, locationId?: string) =>
    [...fuelTransactionKeys.commitments(), organizationId, locationId] as const,
}

/**
 * Fetch all fuel transactions for current organization with optional filtering
 */
export function useFuelTransactions(filters?: FuelTransactionFilters, recordStatus: RecordStatusFilter = 'active') {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  const filtersWithStatus: FuelTransactionFilters = {
    ...filters,
    recordStatus,
  }

  return useQuery({
    queryKey: fuelTransactionKeys.list(organizationId || '', filters, recordStatus),
    queryFn: () => fetchFuelTransactions(organizationId!, filtersWithStatus),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single fuel transaction by ID
 */
export function useFuelTransactionById(id: string | undefined) {
  return useQuery({
    queryKey: fuelTransactionKeys.detail(id || ''),
    queryFn: () => fetchFuelTransactionById(id!),
    enabled: !!id,
  })
}

/**
 * Fetch fuel summary for a specific aircraft
 * Used in FuelTab of ExpandingCard
 */
export function useAircraftFuelSummary(
  aircraftId: string | undefined,
  contractId?: string
) {
  return useQuery({
    queryKey: fuelTransactionKeys.summary(aircraftId || '', contractId),
    queryFn: () => fetchAircraftFuelSummary(aircraftId!, contractId),
    enabled: !!aircraftId,
  })
}

/**
 * Fetch fuel commitment progress for all contracts
 * Used in commitment dashboards and reports
 */
export function useFuelCommitmentProgress(locationId?: string) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: fuelTransactionKeys.commitment(organizationId || '', locationId),
    queryFn: () => fetchFuelCommitmentProgress(organizationId!, locationId),
    enabled: !!organizationId,
  })
}

/**
 * Create a new fuel transaction
 */
export function useCreateFuelTransaction() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (data: FuelTransactionFormData) => {
      return createFuelTransaction(currentOrganization!.id, data)
    },
    onSuccess: (result) => {
      const keys = [fuelTransactionKeys.lists(), fuelTransactionKeys.summary(result.aircraft_id), fuelTransactionKeys.commitments()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      broadcastInvalidation([...keys, ['map-allocations'] as const])
      toast.success('Fuel transaction created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create fuel transaction', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing fuel transaction
 */
export function useUpdateFuelTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<FuelTransactionFormData>
    }) => {
      return updateFuelTransaction(id, data)
    },
    onSuccess: (result, { id }) => {
      const keys = [fuelTransactionKeys.lists(), fuelTransactionKeys.detail(id), fuelTransactionKeys.summary(result.aircraft_id), fuelTransactionKeys.commitments()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      broadcastInvalidation([...keys, ['map-allocations'] as const])
      toast.success('Fuel transaction updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update fuel transaction', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a fuel transaction (soft delete)
 */
export function useDeleteFuelTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteFuelTransaction(id),
    onSuccess: () => {
      const keys = [fuelTransactionKeys.lists(), fuelTransactionKeys.summaries(), fuelTransactionKeys.commitments()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      broadcastInvalidation([...keys, ['map-allocations'] as const])
      toast.success('Fuel transaction deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete fuel transaction', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted fuel transaction
 */
export function useRestoreFuelTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => restoreFuelTransaction(id),
    onSuccess: () => {
      const keys = [fuelTransactionKeys.lists(), fuelTransactionKeys.summaries(), fuelTransactionKeys.commitments()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      broadcastInvalidation([...keys, ['map-allocations'] as const])
      toast.success('Fuel transaction restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore fuel transaction', {
        description: error.message,
      })
    },
  })
}
